import jwtDecode from 'jwt-decode';
import Cookies from 'universal-cookie';

// Defaults
// NATWEB also uses this same cookie, so if changes are made need to coordinate with them
const DEFAULT_TOKEN_NAME = 'FOXSSOACCT';
const OLD_DEFAULT_DOMAIN = '.foxnews.com';
const NATSSO_TOKEN_NAME = 'NATSSO';
const NATSSO_DOMAIN_NAME = '.foxnews.com';
const PREPROD_TOKEN_NAME = 'qFOXSSOACCT';
const SECURE_PREFIX = '__Secure-';

const COOKIE_DOMAIN_NATION_REGEX = /^\.((qa|staging|dev)-)?nation/;

export const DEFAULT_AUTH_OPTIONS = {
  name: DEFAULT_TOKEN_NAME,
  path: '/',
  preProd: false,
  secure: true,
};

// Auth (simple sso)
class Auth {
  jwt;
  options = DEFAULT_AUTH_OPTIONS;
  store;
  token;

  constructor() {
    this.store = new Cookies();
  }

  logError(error) {
    console.error(error);
  }

  getHostname() {
    return window?.location?.hostname || '';
  }

  getCookieDomain(cookieName) {
    const hostname = this.getHostname();

    let domain = this.options?.domain;
    // if no domain specified in options
    if (!domain) {
      // add dot prefix to support subdomains for non-localhost hostname
      // if this is local, just return the hostname (presumably 'localhost') directly
      if (hostname.includes('local')) return hostname;
      domain = `.${hostname}`;
    }
    if (
      cookieName === DEFAULT_TOKEN_NAME &&
      !COOKIE_DOMAIN_NATION_REGEX.test(domain) &&
      COOKIE_DOMAIN_NATION_REGEX.test(`.${hostname}`)
    ) {
      domain = `.${hostname.split('.')[0]}${domain}`;
    }
    return domain;
  }

  configure(config = {}) {
    this.options = Object.assign({}, this.options, config);
    if (this.options?.preProd) {
      this.options.name = PREPROD_TOKEN_NAME;
    }
    this.checkSecure();
    this.loadCookie();
    return this.token;
  }

  getToken() {
    return this.token;
  }

  setToken(token) {
    try {
      if (token && this.options?.name) {
        const expires = token?.tokenExpiration ? new Date(token.tokenExpiration) : undefined;
        const domain = this.getCookieDomain(this.options.name);
        const options = { ...this.options, domain, expires };

        if (token?.accessToken) {
          this.jwt = jwtDecode(token?.accessToken);
        }
        this.token = token;
        this.store.set(this.options?.name, token, options);
        const { accessToken, profileId, refreshToken, tokenExpiration } = token;
        this.store.set(
          NATSSO_TOKEN_NAME,
          { accessToken, profileId, refreshToken, tokenExpiration },
          {
            ...options,
            domain: this.getHostname().includes('local') ? null : NATSSO_DOMAIN_NAME,
          }
        );
      }
    } catch (error) {
      this.logError(error);
    }
  }

  removeToken() {
    if (this.options?.name) {
      const domain = this.getCookieDomain(this.options.name);
      this.store.remove(this.options?.name, { ...this.options, domain });
      this.store.remove(this.options?.name);
      this.store.remove(NATSSO_TOKEN_NAME, { ...this.options, domain: NATSSO_DOMAIN_NAME });
      this.store.remove(NATSSO_TOKEN_NAME);
    }
    this.token = undefined;
    this.jwt = undefined;
    return undefined;
  }

  getMvpdId() {
    return this.jwt?.mvpdid;
  }

  getUserId() {
    return this.jwt?.uid;
  }

  getDeviceId() {
    return this.jwt?.did;
  }

  getUserType() {
    return this.jwt?.utype;
  }

  getDeviceType() {
    return this.jwt?.dtype;
  }

  getAccountType() {
    return this.jwt?.atype;
  }

  getAccessToken() {
    return this.token?.accessToken;
  }

  getMvpdAccessToken() {
    if (this.isMvpdUser()) {
      return this.token?.accessToken;
    }
    return undefined;
  }

  getNonAnonAccessToken() {
    if (this.isNonAnonymousUser()) {
      return this.token?.accessToken;
    }
    return undefined;
  }

  getAnonDeviceId() {
    const isAnon = this.isAnonymousUser();
    const deviceId = this.getDeviceId();
    if (isAnon) {
      return deviceId;
    }
    return undefined;
  }

  isAnonymousUser() {
    return !!this.jwt?.atype && this.jwt.atype === 'anonymous';
  }

  isNonAnonymousUser() {
    return !!this.jwt?.atype && this.jwt.atype !== 'anonymous';
  }

  isMvpdUser() {
    return this.isAnonymousUser() && !!this.getMvpdId();
  }

  checkSecure() {
    if (this.options?.name && this.options.secure && !this.options.name.includes(SECURE_PREFIX)) {
      this.options.name = SECURE_PREFIX + this.options.name;
      this.options.sameSite = true;
    }
  }

  loadCookie() {
    if (this.options?.name) {
      if (this.options.name === DEFAULT_TOKEN_NAME) {
        // check for an existing FOXSSOACCT cookie and whether it's associated with a deprecated domain
        //    if so, copy the contents of that cookie to under the new domain and delete the old
        const oldCookie = new Cookies({ domain: OLD_DEFAULT_DOMAIN });
        const oldToken = oldCookie.get(this.options.name);
        if (oldToken !== undefined) {
          this.setToken(oldToken);
          oldCookie.remove(this.options.name, { ...this.options, domain: OLD_DEFAULT_DOMAIN });
          return;
        }
      }
      const token = this.store.get(this.options.name);
      this.setToken(token);
    }
  }
}
const AuthStore = new Auth();
export { AuthStore };
